import * as React from "react";
import { useMemo, useState } from "react";
import { Button, Box, Chip, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ColDef } from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import { formatInTimeZone } from "date-fns-tz";
import { ApiRequestFailed, authenticateAndFetchData, retryFn } from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import ServerSideDataTable from "@components/ServerSideDataTable";
import { Downloading } from "@mui/icons-material";
import { exportToCSV, CSVColumn } from "@components/ExportToCSV";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";
import {useSnackbar} from "notistack";

const ADMIN_REFERENCE_TIMEZONE = "Asia/Kolkata";

export type DayStatus = "sent" | "partial" | "scheduled" | "cancelled";

export interface DayLog {
    day_number: number;
    date: string;
    window_start: string | null;
    window_end: string | null;
    scheduled: number;
    sent: number;
    replied: number;
    bounced: number;
    unsubscribed: number;
    failed: number;
    pending: number;
    cancelled: number;
    cancelled_breakdown: {
        reply_received: number;
        campaign_stopped: number;
        esp_blocked: number;
    };
    status: DayStatus;
}

export interface DailyLogSummary {
    timezone: string;
    days_remaining: number;
    total_domains: number;
    campaign_start_date: string | null;
    campaign_end_date: string | null;
    current_day_number: number;
    total_campaign_days: number;
}

export interface DailyLogResponse {
    summary: DailyLogSummary;
    days: DayLog[];
}

interface Props {
    campaignUid: string;
}

/**
 * Shared between the log table and the campaign analytics stat cards. Both call it with
 * the same key so TanStack Query dedupes them into a single request.
 */
export function useCampaignDailyLog(campaignUid: string) {
    return useQuery<DailyLogResponse, ApiRequestFailed>({
        queryKey: ["admin-campaign-daily-log", campaignUid],
        queryFn: async () => {
            const response = await authenticateAndFetchData(
                `/admin/get-campaign-daily-log/?campaign_uid=${campaignUid}`
            );
            return response.data as DailyLogResponse;
        },
        retry: retryFn,
    });
}

function statusColor(status: DayStatus): "success" | "warning" | "error" | "default" {
    if (status === "sent") return "success";
    if (status === "partial") return "warning";
    if (status === "cancelled") return "error";
    return "default";
}

function statusLabel(status: DayStatus): string {
    if (status === "sent") return "Sent";
    if (status === "partial") return "Partial";
    if (status === "cancelled") return "Cancelled";
    return "Scheduled";
}

/**
 * A campaign-local sending day can straddle midnight in the display timezone, so the
 * end date is only omitted when the window genuinely stays within one local date.
 */
function formatWindow(start: string | null, end: string | null, timeZone: string): string {
    if (!start || !end) return "—";
    const from = new Date(start);
    const to = new Date(end);
    const fromDate = formatInTimeZone(from, timeZone, "d MMM");
    const toDate = formatInTimeZone(to, timeZone, "d MMM");
    const fromTime = formatInTimeZone(from, timeZone, "h:mm a");
    const toTime = formatInTimeZone(to, timeZone, "h:mm a");

    return fromDate === toDate
        ? `${fromDate}, ${fromTime} – ${toTime}`
        : `${fromDate} ${fromTime} – ${toDate} ${toTime}`;
}

function DayStatusCell({ data }: CustomCellRendererProps<DayLog>) {
    if (!data?.status) return null;
    return <Chip label={statusLabel(data.status)} color={statusColor(data.status)} size="small" sx={{ fontWeight: 600 }} />;
}

function CancelledCell({ data, value }: CustomCellRendererProps<DayLog>) {
    const formatted = (value ?? 0).toLocaleString();
    // The pinned totals row carries no breakdown, so it renders without a tooltip.
    if (!data?.cancelled_breakdown) return <>{formatted}</>;
    const { reply_received, campaign_stopped, esp_blocked } = data.cancelled_breakdown;
    return (
        <Tooltip title={`Reply received: ${reply_received} · Campaign stopped: ${campaign_stopped} · ESP switched off: ${esp_blocked ?? 0}`}>
            <span>{formatted}</span>
        </Tooltip>
    );
}

const numberCol = (field: keyof DayLog, headerName: string, width: number): ColDef<DayLog> => ({
    field,
    headerName,
    width,
    flex: 0,
    type: "numericColumn",
    valueFormatter: (p) => (p.value ?? 0).toLocaleString(),
});

export default function AdminCampaignDailyLog({ campaignUid }: Props) {
    const { user } = useAuth();
    const dailyLogQuery = useCampaignDailyLog(campaignUid);
    const summary = dailyLogQuery.data?.summary;
    const days = useMemo(() => dailyLogQuery.data?.days ?? [], [dailyLogQuery.data]);

    const campaignTz = summary?.timezone || "UTC";

    const columns = useMemo<ColDef<DayLog>[]>(() => {
        return [
            {
                field: "day_number",
                headerName: "Day",
                width: 90,
                flex: 0,
                pinned: "left",
                valueFormatter: (p) => (p.node?.rowPinned ? "Total" : `Day ${p.value}`),
                cellStyle: { fontWeight: 600 },
            },
            { field: "status", headerName: "Status", width: 120, flex: 0, cellRenderer: DayStatusCell },
            {
                colId: "window_campaign_tz",
                headerName: `Date & time (${campaignTz})`,
                width: 210,
                flex: 0,
                valueGetter: (p) => (p.data ? formatWindow(p.data.window_start, p.data.window_end, campaignTz) : ""),
            },
            {
                colId: "window_ist",
                headerName: "Date & time (IST)",
                width: 210,
                flex: 0,
                valueGetter: (p) =>
                    p.data ? formatWindow(p.data.window_start, p.data.window_end, ADMIN_REFERENCE_TIMEZONE) : "",
            },
            { ...numberCol("scheduled", "Scheduled", 115), cellStyle: { fontWeight: 700 } },
            numberCol("sent", "Sent", 90),
            numberCol("replied", "Replied", 100),
            numberCol("bounced", "Bounced", 105),
            numberCol("unsubscribed", "Unsubscribed", 135),
            numberCol("failed", "Failed", 90),
            numberCol("pending", "Pending", 100),
            { ...numberCol("cancelled", "Cancelled", 110), cellRenderer: CancelledCell },
        ];
    }, [campaignTz]);

    const totalsRow = useMemo(() => {
        if (days.length === 0) return [];
        const sum = (key: keyof DayLog) => days.reduce((acc, d) => acc + (d[key] as number), 0);
        return [
            {
                day_number: null,
                status: null,
                scheduled: sum("scheduled"),
                sent: sum("sent"),
                replied: sum("replied"),
                bounced: sum("bounced"),
                unsubscribed: sum("unsubscribed"),
                failed: sum("failed"),
                pending: sum("pending"),
                cancelled: sum("cancelled"),
            },
        ];
    }, [days]);

    const campaignDailyLogColumns: CSVColumn<DayLog>[] = useMemo(
        () => [
            { 
                header: "Day", 
                accessor: (row) => `Day ${row.day_number}` 
            },
            { 
                header: "Status", 
                accessor: "status" 
            },
            { 
                header: "Date & Time (Campaign TZ)", 
                accessor: (row) => formatWindow(row.window_start, row.window_end, campaignTz) 
            },
            { 
                header: "Date & Time (IST)", 
                accessor: (row) => formatWindow(row.window_start, row.window_end, ADMIN_REFERENCE_TIMEZONE) 
            },
            { header: "Scheduled", accessor: "scheduled" },
            { header: "Sent", accessor: "sent" },
            { header: "Replied", accessor: "replied" },
            { header: "Bounced", accessor: "bounced" },
            { header: "Unsubscribed", accessor: "unsubscribed" },
            { header: "Failed", accessor: "failed" },
            { header: "Pending", accessor: "pending" },
            { header: "Cancelled", accessor: "cancelled" },
        ],
        [campaignTz]
    );

    const ExportCampaignDailyLogCsvButton = () => {
        const [exportLoading, setExportLoading] = useState(false);
        const { enqueueSnackbar } = useSnackbar();

        const handleExportDailyLogs = async () => {
            setExportLoading(true);

            try {
                const response = await authenticateAndFetchData(
                    `/admin/get-campaign-daily-log/?campaign_uid=${campaignUid}&export_all=true`
                );

                const logsToExport = response?.data?.days || response?.data?.data || [];

                if (!logsToExport.length) {
                    enqueueSnackbar("No daily log data available to export.", { variant: "error" });
                    return;
                }
                
                const formattedColumns = campaignDailyLogColumns.map((col) => {
                    if (typeof col.accessor === "function") {
                        return {
                            ...col,
                            accessor: (row: any) => (col.accessor as Function)(row, campaignTz),
                        };
                    }
                    return col;
                });

                const fileName = getExportFilename(user?.email ?? "unknown",
                    "DailyLogs" 
                );

                const success = exportToCSV(
                    logsToExport,
                    formattedColumns,
                    fileName
                );

                if (success) {
                    enqueueSnackbar("Daily logs exported successfully!", { variant: "success" });
                } else {
                    enqueueSnackbar("Failed to generate CSV file.", { variant: "error" });
                }
            } catch (err) {
                console.error("Export error:", err);
                enqueueSnackbar("Error while exporting daily logs.", { variant: "error" });
            } finally {
                setExportLoading(false);
            }
        };

        return (
            <Button
                startIcon={<Downloading />}
                variant="contained"
                color="primary"
                onClick={handleExportDailyLogs}
                disabled={exportLoading}
            >
                {exportLoading ? "Exporting..." : "Export"}
            </Button>
        );
    };

    if (dailyLogQuery.isLoading) return <PageLoading />;
    if (dailyLogQuery.isError) return <PageDataErrorHandler error={dailyLogQuery.error as ApiRequestFailed} />;
    if (!summary) return null;

    const progressPct = summary.total_campaign_days > 0
        ? Math.round((summary.current_day_number / summary.total_campaign_days) * 100)
        : 0;

    return (
        <Stack spacing={3}>
            {summary.total_campaign_days > 0 && (
                <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                            Day {summary.current_day_number} of {summary.total_campaign_days} sending days
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {progressPct}%
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progressPct} sx={{ height: 8, borderRadius: 4 }} />
                </Box>
            )}

            <Typography variant="caption" color="text.secondary">
                Days are grouped by the campaign's own timezone ({campaignTz}).
            </Typography>

            <Box sx={{ height: 600 }}>
                <ServerSideDataTable
                    columns={columns as ColDef[]}
                    rows={days}
                    pinnedBottomRowData={totalsRow}
                    getRowId={(params) => String(params.data.day_number)}
                    noRowsText="No sending data available yet."
                    actions={<ExportCampaignDailyLogCsvButton />}
                />
            </Box>
        </Stack>
    );
}
